import {
  type ColumnDef,
  type ColumnFiltersState,
  type ColumnVisibilityState,
  columnFilteringFeature,
  columnVisibilityFeature,
  createCoreRowModel,
  createFilteredRowModel,
  createPaginatedRowModel,
  createSortedRowModel,
  filterFns,
  flexRender,
  functionalUpdate,
  globalFilteringFeature,
  type PaginationState,
  type RowData,
  type RowSelectionState,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  tableFeatures,
  useTable,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/components/ui";
import { cn } from "~/lib/utils";

/** Module-level features registry. Sort + global/column filter + pagination + visibility + selection are on. */
export const dtFeatures = tableFeatures({
  rowSortingFeature,
  rowPaginationFeature,
  globalFilteringFeature,
  columnFilteringFeature,
  columnVisibilityFeature,
  rowSelectionFeature,
  coreRowModel: createCoreRowModel(),
  sortedRowModel: createSortedRowModel(),
  filteredRowModel: createFilteredRowModel(),
  paginatedRowModel: createPaginatedRowModel(),
  sortFns: { alphanumeric: sortFn_alphanumeric },
  filterFns,
});

export type DataTableFeatures = typeof dtFeatures;

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

interface DataTableProps<TData extends RowData> {
  columns: ColumnDef<DataTableFeatures, TData, any>[];
  data: TData[];
  emptyMessage?: string;
  pageSize?: number;
  /** Controlled page-size selector. When omitted, the page size is fixed at `pageSize`. */
  onPageSizeChange?: (next: number) => void;
  className?: string;
  /** Controlled global (free-text) filter. When provided, the parent owns the value. */
  globalFilter?: string;
  onGlobalFilterChange?: (next: string) => void;
  /** Controlled column-filter state. When provided, the parent owns the value. */
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: (next: ColumnFiltersState) => void;
  /** Controlled page index (0-based). When provided, the parent owns it (URL-sync). */
  pageIndex?: number;
  onPageIndexChange?: (next: number) => void;
  /** Controlled column-visibility map. When provided, the parent owns the menu UI. */
  columnVisibility?: ColumnVisibilityState;
  onColumnVisibilityChange?: (next: ColumnVisibilityState) => void;
  /** Opt-in: navigate or select on full-row click. Adds a cursor-pointer affordance. */
  onRowClick?: (row: TData) => void;
  /**
   * Controlled row selection, keyed by `getRowId`. Providing the pair enables
   * selection; render checkbox column(s) via the row/table selection APIs.
   */
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (next: RowSelectionState) => void;
  /** Stable row id for selection keys (defaults to the row index). */
  getRowId?: (row: TData) => string;
}

/**
 * Minimal shadcn-styled TanStack Table wrapper (v9). Sort, global/column
 * filter, and client-side pagination are wired in; the wrapper supports
 * both controlled (URL-driven) and uncontrolled state for each axis.
 */
export function DataTable<TData extends RowData>({
  columns,
  data,
  emptyMessage,
  pageSize = DEFAULT_PAGE_SIZE,
  onPageSizeChange,
  className,
  globalFilter,
  onGlobalFilterChange,
  columnFilters,
  onColumnFiltersChange,
  pageIndex,
  onPageIndexChange,
  columnVisibility,
  onColumnVisibilityChange,
  onRowClick,
  rowSelection,
  onRowSelectionChange,
  getRowId,
}: DataTableProps<TData>) {
  const { t } = useTranslation();

  const controlledPagination: PaginationState | undefined = useMemo(
    () => (pageIndex !== undefined ? { pageIndex, pageSize } : undefined),
    [pageIndex, pageSize],
  );

  const initialState = useMemo(() => ({ pagination: { pageIndex: 0, pageSize } }), [pageSize]);

  const state = useMemo(
    () => ({
      ...(globalFilter !== undefined ? { globalFilter } : {}),
      ...(columnFilters !== undefined ? { columnFilters } : {}),
      ...(controlledPagination ? { pagination: controlledPagination } : {}),
      ...(columnVisibility !== undefined ? { columnVisibility } : {}),
      ...(rowSelection !== undefined ? { rowSelection } : {}),
    }),
    [globalFilter, columnFilters, controlledPagination, columnVisibility, rowSelection],
  );

  const onGlobalFilterChangeAdapter = useCallback(
    (updater: unknown) => {
      if (!onGlobalFilterChange) return;
      const next = functionalUpdate(updater as never, globalFilter ?? "");
      onGlobalFilterChange(next as string);
    },
    [onGlobalFilterChange, globalFilter],
  );

  const onColumnFiltersChangeAdapter = useCallback(
    (updater: unknown) => {
      if (!onColumnFiltersChange) return;
      const next = functionalUpdate(updater as never, columnFilters ?? []);
      onColumnFiltersChange(next as ColumnFiltersState);
    },
    [onColumnFiltersChange, columnFilters],
  );

  const onPaginationChangeAdapter = useCallback(
    (updater: unknown) => {
      if (!onPageIndexChange) return;
      const next = functionalUpdate(updater as never, controlledPagination ?? { pageIndex: 0, pageSize });
      onPageIndexChange((next as PaginationState).pageIndex);
    },
    [onPageIndexChange, controlledPagination, pageSize],
  );

  const onColumnVisibilityChangeAdapter = useCallback(
    (updater: unknown) => {
      if (!onColumnVisibilityChange) return;
      const next = functionalUpdate(updater as never, columnVisibility ?? {});
      onColumnVisibilityChange(next as ColumnVisibilityState);
    },
    [onColumnVisibilityChange, columnVisibility],
  );

  const onRowSelectionChangeAdapter = useCallback(
    (updater: unknown) => {
      if (!onRowSelectionChange) return;
      const next = functionalUpdate(updater as never, rowSelection ?? {});
      onRowSelectionChange(next as RowSelectionState);
    },
    [onRowSelectionChange, rowSelection],
  );

  const table = useTable<DataTableFeatures, TData>({
    features: dtFeatures,
    initialState,
    state,
    // Keep the controlled/URL-backed page when `data` gets a new reference
    // (e.g. after navigating to a detail route and back). Without this, a new
    // `data` array resets the page index to 0.
    autoResetPageIndex: false,
    onGlobalFilterChange: onGlobalFilterChange ? onGlobalFilterChangeAdapter : undefined,
    onColumnFiltersChange: onColumnFiltersChange ? onColumnFiltersChangeAdapter : undefined,
    onPaginationChange: onPageIndexChange ? onPaginationChangeAdapter : undefined,
    onColumnVisibilityChange: onColumnVisibilityChange ? onColumnVisibilityChangeAdapter : undefined,
    onRowSelectionChange: onRowSelectionChange ? onRowSelectionChangeAdapter : undefined,
    enableRowSelection: rowSelection !== undefined && onRowSelectionChange !== undefined,
    getRowId,
    data,
    columns,
  });

  const rows = table.getRowModel().rows;
  const totalRowCount = table.getFilteredRowModel().rows.length;
  const visibleColumnCount = table.getVisibleLeafColumns().length;
  const currentPageIndex = controlledPagination?.pageIndex ?? table.state.pagination?.pageIndex ?? 0;
  const showPagination = totalRowCount > pageSize;
  const rangeFrom = totalRowCount === 0 ? 0 : currentPageIndex * pageSize + 1;
  const rangeTo = Math.min(totalRowCount, (currentPageIndex + 1) * pageSize);

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <div className="overflow-x-auto rounded-[var(--radius-md)] border border-border-default bg-white">
        <table className="w-full min-w-max border-collapse">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="bg-bg-subtle">
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  return (
                    <th
                      key={header.id}
                      className="border-b border-border-default px-3.5 py-2.5 text-left text-[11px] font-bold uppercase tracking-[0.06em] text-fg-2"
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="inline-flex items-center gap-1.5 text-left uppercase tracking-[0.06em] text-fg-2 hover:text-fg-1 focus:outline-none focus-visible:text-primary-700"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {sorted === "asc" ? (
                            <ArrowUp className="h-3 w-3" />
                          ) : sorted === "desc" ? (
                            <ArrowDown className="h-3 w-3" />
                          ) : (
                            <ArrowUpDown className="h-3 w-3 opacity-60" />
                          )}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={visibleColumnCount} className="px-3.5 py-10 text-center text-[13px] text-fg-3">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={row.id}
                  className={cn(
                    "border-b border-border-default last:border-b-0 hover:bg-primary-50",
                    onRowClick && "cursor-pointer",
                  )}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-3.5 py-3 text-[13px] align-middle">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showPagination && (
        <div className="flex flex-wrap items-center justify-between gap-3 text-[12px] text-fg-3">
          <div className="flex items-center gap-3">
            <div className="tabular-nums">
              {t("ui.pagination.range", { from: rangeFrom, to: rangeTo, total: totalRowCount })}
            </div>
            {onPageSizeChange && (
              <div className="flex items-center gap-1.5">
                <span>{t("ui.pagination.rowsPerPage")}</span>
                <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
                  <SelectTrigger className="h-7 w-[72px]" aria-label={t("ui.pagination.rowsPerPage")}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((opt) => (
                      <SelectItem key={opt} value={String(opt)}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <ChevronLeft />
              <span>{t("ui.pagination.previous")}</span>
            </Button>
            <Button variant="outline" size="sm" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
              <span>{t("ui.pagination.next")}</span>
              <ChevronRight />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
